import React, { useCallback } from 'react';
import { ValidOption, ValidOptionalInput } from './types';
import ValidInputError from './ValidInputError';
import ValidErrorMarker from './ValidErrorMarker';

// TYPE
export type ValidPropRadioText = {
    key: string;
    lbl: string;
    val: string;
    def: string;
    options: ValidOption[];
    err: string;
};

// VIEW MODEL
export function validPropRadioTextVM(
    key: string,
    lbl?: string,
    val?: any,
    def?: string,
    options?: ValidOption[],
): ValidPropRadioText {
    return {
        key: key,
        lbl: !!lbl ? lbl : '',
        val,
        def: def || '',
        options: Array.isArray(options) ? options : [],
        err: '',
    };
}

// VALIDATOR
export function validatePropRadioText(prop: ValidPropRadioText, req?: boolean): boolean {
    prop.err = '';

    const val = typeof prop.val === 'string' ? prop.val.trim() : '';
    if (!!req && val.length < 1) {
        prop.err = 'required';
    }

    return !!!prop.err;
}

// COMPONENT
export type ValidInputRadioTextProps = ValidOptionalInput & {
    prop: ValidPropRadioText;
    disabled?: boolean;
    onChange: (prop: string, val: string) => void;
    fullWidth?: boolean;
    alternateLabel?: string;
    interpretAs?: 'string' | 'number';
};

const ValidInputRadioText = ({
    prop,
    disabled,
    onChange,
    checked,
    onCheckboxChange,
    fullWidth,
    alternateLabel,
    interpretAs = 'string',
}: ValidInputRadioTextProps): JSX.Element => {
    const radioGroup = 'vpRadioTextGrp' + prop.key;
    const showCheckbox = !!onCheckboxChange && checked !== undefined;
    const label = alternateLabel ? alternateLabel : prop.lbl;

    const handleCheckboxChange = useCallback((): void => {
        if (showCheckbox) {
            onCheckboxChange(!checked);
        }
    }, [checked, onCheckboxChange, showCheckbox]);

    const handleChange = useCallback(
        (e): void => {
            const newValue = interpretAs === 'number' ? parseInt(e.target.value) : e.target.value;
            if (!disabled) {
                onChange(prop.key, newValue);
            }
        },
        [disabled, interpretAs, onChange, prop.key],
    );

    return (
        <div className="component-valid-input">
            <div className={`input-wrapper ${fullWidth ? 'full-width' : ''}`}>
                {label ? (
                    <label
                        className="valid-input-label"
                        onClick={showCheckbox ? handleCheckboxChange : undefined}
                    >
                        {showCheckbox ? (
                            <span className={`icon-glyph ${checked ? 'icon-checked' : 'icon-unchecked'}`} />
                        ) : null}
                        {label}
                    </label>
                ) : null}
                <ul className="valid-input input-radio">
                    {prop.options.map((opt, idx) => {
                        return (
                            <li key={idx}>
                                <label>
                                    <input
                                        type="radio"
                                        id={radioGroup + idx.toString()}
                                        name={radioGroup}
                                        value={opt.val}
                                        checked={opt.val == prop.val}
                                        disabled={disabled}
                                        onChange={handleChange}
                                    />{' '}
                                    {opt.lbl}
                                </label>
                            </li>
                        );
                    })}
                </ul>
                <ValidErrorMarker showMarker={!!prop.err} />
            </div>
            <ValidInputError
                err={prop.err}
                showMsg
            />
        </div>
    );
};

export default ValidInputRadioText;
